/**
 * WHAT: PostgreSQL password security report module.
 * WHY: PostgreSQL lacks built-in password profiles unlike Oracle, so this module provides
 *      a dedicated security report focused on role attributes, password expiration,
 *      connection limits, and server-level authentication settings.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_CONFIG from '../../config/api.config';
import { WideModal, PostgresqlIcon } from '../common';

/**
 * WHAT: PostgreSQL password and authentication security dashboard with summary, role details, guidelines, and server settings.
 * WHY: PostgreSQL lacks built-in password profiles, so this dedicated component focuses on role security attributes,
 *      password expiration, and server-level authentication settings rather than CIS profile benchmarks.
 */
const PostgresqlPasswordSecurity = () => {
  // State for data
  const [databases, setDatabases] = useState([]);
  const [summary, setSummary] = useState(null);
  const [roles, setRoles] = useState([]);
  const [rules, setRules] = useState([]);
  const [serverSettings, setServerSettings] = useState([]);

  // State for UI
  const [activeTab, setActiveTab] = useState('summary');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDatabase, setSelectedDatabase] = useState('');
  const [selectedRole, setSelectedRole] = useState(null);

  // Filter state
  const [filters, setFilters] = useState({
    filter_type: 'all',
    search: ''
  });

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 50;

  /* WHAT: Fetches the list of registered PostgreSQL databases for the filter dropdown.
     WHY: Users can scope the security report to a single database or view all databases at once. */
  const fetchDatabases = useCallback(async () => {
    try {
      const response = await axios.get(
        API_CONFIG.getDatabasesEndpoint('postgresql-security/databases/')
      );
      if (response.data.success) {
        setDatabases(response.data.databases || []);
      }
    } catch (err) {
      console.error('Error fetching databases:', err);
    }
  }, []);

  /* WHAT: Fetches aggregate security metrics (role counts, risk distribution, expiration counts).
     WHY: The summary tab gives a quick at-a-glance view of the overall PostgreSQL security posture. */
  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = selectedDatabase ? `?database_id=${selectedDatabase}` : '';
      const response = await axios.get(
        API_CONFIG.getDatabasesEndpoint(`postgresql-security/summary/${params}`)
      );
      if (response.data.success) {
        setSummary(response.data.summary);
      }
    } catch (err) {
      setError('Failed to fetch security summary');
      console.error('Error fetching summary:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedDatabase]);

  /* WHAT: Fetches paginated PostgreSQL roles with their security attributes, filtered by type/search.
     WHY: The roles tab lists all database roles with their privileges and risk levels for security review. */
  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        API_CONFIG.getDatabasesEndpoint('postgresql-security/roles/'),
        {
          database_id: selectedDatabase || null,
          filter_type: filters.filter_type,
          search: filters.search,
          page: page,
          page_size: pageSize
        }
      );
      if (response.data.success) {
        setRoles(response.data.roles || []);
        setTotalCount(response.data.total_count || 0);
        setTotalPages(response.data.total_pages || 1);
      }
    } catch (err) {
      console.error('Error fetching roles:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedDatabase, filters, page]);

  /* WHAT: Fetches PostgreSQL security guideline rules (best practices, not CIS benchmark rules).
     WHY: PostgreSQL lacks CIS password profile benchmarks, so these guidelines provide actionable security recommendations. */
  const fetchRules = useCallback(async () => {
    try {
      const response = await axios.get(
        API_CONFIG.getDatabasesEndpoint('postgresql-security/rules/')
      );
      if (response.data.success) {
        setRules(response.data.rules || []);
      }
    } catch (err) {
      console.error('Error fetching rules:', err);
    }
  }, []);

  /* WHAT: Fetches server-level authentication settings (password encryption method, credcheck config, etc.).
     WHY: Server settings determine the baseline security posture; weak settings like md5 are flagged for remediation. */
  const fetchServerSettings = useCallback(async () => {
    try {
      const params = selectedDatabase ? `?database_id=${selectedDatabase}` : '';
      const response = await axios.get(
        API_CONFIG.getDatabasesEndpoint(`postgresql-security/settings/${params}`)
      );
      if (response.data.success) {
        setServerSettings(response.data.databases || []);
      }
    } catch (err) {
      console.error('Error fetching server settings:', err);
    }
  }, [selectedDatabase]);

  /* WHAT: Triggers a CSV export of the security report by redirecting to the backend export endpoint.
     WHY: DBAs need downloadable reports for offline review, sharing with auditors, or compliance documentation. */
  const handleExportCSV = async () => {
    try {
      const params = selectedDatabase ? `?database_id=${selectedDatabase}` : '';
      window.location.href = API_CONFIG.getDatabasesEndpoint(`postgresql-security/export/csv/${params}`);
    } catch (err) {
      console.error('Error exporting CSV:', err);
    }
  };

  // Load data on mount
  useEffect(() => {
    fetchDatabases();
    fetchRules();
  }, [fetchDatabases, fetchRules]);

  // Reload data when database filter changes
  useEffect(() => {
    fetchSummary();
    fetchServerSettings();
  }, [fetchSummary, fetchServerSettings]);

  // Reload roles when filters change
  useEffect(() => {
    if (activeTab === 'roles') {
      fetchRoles();
    }
  }, [activeTab, fetchRoles]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [selectedDatabase, filters.filter_type, filters.search]);

  /* WHAT: Renders a risk level (CRITICAL/HIGH/MEDIUM/LOW) as colored text with a dot indicator.
     WHY: Consistent risk-level rendering across the security report following the DAMA text-color styling standard. */
  const getRiskText = (level) => {
    const styles = {
      'CRITICAL': 'text-danger',
      'HIGH': 'text-warning',
      'MEDIUM': 'text-info',
      'LOW': 'text-secondary'
    };
    return (
      <span className={styles[level] || 'text-secondary'}>
        <i className="fas fa-circle fa-xs me-1"></i>{level}
      </span>
    );
  };

  /* WHAT: Renders a password expiry status (Expired, Expiring Soon, Never Expires, Valid) with appropriate color.
     WHY: Expiry status is a key security indicator; color-coding makes expired/expiring passwords immediately visible. */
  const getExpiryText = (status) => {
    const styles = {
      'Expired': 'text-danger',
      'Expiring Soon': 'text-warning',
      'Never Expires': 'text-info',
      'Valid': 'text-success',
      'N/A': 'text-muted'
    };
    return <span className={styles[status] || 'text-muted'}>{status}</span>;
  };

  /* WHAT: Renders first/prev/next/last pagination controls for the roles table.
     WHY: Role lists can be large; server-side pagination keeps the UI responsive while allowing navigation. */
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <nav>
        <ul className="pagination pagination-sm justify-content-center mb-0">
          <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => setPage(1)}>First</button>
          </li>
          <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => setPage(p => Math.max(1, p - 1))}>Prev</button>
          </li>
          <li className="page-item disabled">
            <span className="page-link">Page {page} of {totalPages}</span>
          </li>
          <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next</button>
          </li>
          <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => setPage(totalPages)}>Last</button>
          </li>
        </ul>
      </nav>
    );
  };

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            <PostgresqlIcon size={28} className="me-2" />
            PostgreSQL Password Security
          </h2>
          <p className="text-muted mb-0">
            Review authentication settings and security compliance
          </p>
        </div>
        <div className="d-flex gap-2">
          <select
            className="form-select"
            style={{ width: '200px' }}
            value={selectedDatabase}
            onChange={(e) => setSelectedDatabase(e.target.value)}
          >
            <option value="">All Databases</option>
            {databases.map(db => (
              <option key={db.id} value={db.id}>{db.db_name} ({db.host})</option>
            ))}
          </select>
          <button className="btn btn-outline-secondary" onClick={handleExportCSV}>
            <i className="fas fa-download me-2"></i>Export CSV
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'summary' ? 'active' : ''}`}
            onClick={() => setActiveTab('summary')}
          >
            <i className="fas fa-chart-pie me-2"></i>Summary
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'roles' ? 'active' : ''}`}
            onClick={() => setActiveTab('roles')}
          >
            <i className="fas fa-users me-2"></i>
            Roles ({totalCount})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'rules' ? 'active' : ''}`}
            onClick={() => setActiveTab('rules')}
          >
            <i className="fas fa-book me-2"></i>
            Security Guidelines ({rules.length})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <i className="fas fa-cog me-2"></i>
            Server Settings
          </button>
        </li>
      </ul>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {/* Summary Tab */}
      {activeTab === 'summary' && !loading && summary && (
        <div className="row">
          {/* Security Overview */}
          <div className="col-md-8">
            <div className="card mb-4">
              <div className="card-header bg-secondary text-white">
                <h5 className="mb-0"><i className="fas fa-shield-alt me-2"></i>Security Overview</h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-4">
                    <div className="border rounded p-3 text-center mb-3">
                      <h3 className="text-primary mb-0">{summary.total_roles || 0}</h3>
                      <small className="text-muted">Total Roles</small>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="border rounded p-3 text-center mb-3">
                      <h3 className="text-success mb-0">{summary.login_roles || 0}</h3>
                      <small className="text-muted">Can Login</small>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="border rounded p-3 text-center mb-3">
                      <h3 className="text-muted mb-0">{summary.no_login_roles || 0}</h3>
                      <small className="text-muted">Roles Only</small>
                    </div>
                  </div>
                </div>

                <h6 className="mt-3 mb-2">Security Attributes</h6>
                <div className="table-responsive">
                  <table className="table table-sm">
                    <tbody>
                      <tr>
                        <td><i className="fas fa-crown text-danger me-2"></i>Superusers</td>
                        <td className="text-end"><strong className={summary.superuser_count > 2 ? 'text-danger' : 'text-success'}>{summary.superuser_count || 0}</strong></td>
                        <td className="text-muted small">Should be minimal</td>
                      </tr>
                      <tr>
                        <td><i className="fas fa-shield-alt text-warning me-2"></i>Bypass RLS</td>
                        <td className="text-end"><strong className={summary.bypass_rls_count > 0 ? 'text-warning' : 'text-success'}>{summary.bypass_rls_count || 0}</strong></td>
                        <td className="text-muted small">Can bypass row security</td>
                      </tr>
                      <tr>
                        <td><i className="fas fa-database text-info me-2"></i>Can Create DB</td>
                        <td className="text-end"><strong>{summary.can_create_db_count || 0}</strong></td>
                        <td className="text-muted small">Can create databases</td>
                      </tr>
                      <tr>
                        <td><i className="fas fa-user-plus text-info me-2"></i>Can Create Role</td>
                        <td className="text-end"><strong>{summary.can_create_role_count || 0}</strong></td>
                        <td className="text-muted small">Can create/modify roles</td>
                      </tr>
                      <tr>
                        <td><i className="fas fa-sync text-secondary me-2"></i>Replication</td>
                        <td className="text-end"><strong>{summary.replication_count || 0}</strong></td>
                        <td className="text-muted small">Replication privileges</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Risk Distribution */}
          <div className="col-md-4">
            <div className="card mb-4">
              <div className="card-header bg-secondary text-white">
                <h5 className="mb-0"><i className="fas fa-chart-pie me-2"></i>Risk Distribution</h5>
              </div>
              <div className="card-body">
                {summary.risk_distribution && (
                  <div className="d-flex flex-column gap-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-danger"><i className="fas fa-circle fa-xs me-2"></i>Critical</span>
                      <span className="text-danger fw-bold fs-5">{summary.risk_distribution.CRITICAL || 0}</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-warning"><i className="fas fa-circle fa-xs me-2"></i>High</span>
                      <span className="text-warning fw-bold fs-5">{summary.risk_distribution.HIGH || 0}</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-info"><i className="fas fa-circle fa-xs me-2"></i>Medium</span>
                      <span className="text-info fw-bold fs-5">{summary.risk_distribution.MEDIUM || 0}</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-secondary"><i className="fas fa-circle fa-xs me-2"></i>Low</span>
                      <span className="text-secondary fw-bold fs-5">{summary.risk_distribution.LOW || 0}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Password Expiration Summary */}
            <div className="card">
              <div className="card-header bg-secondary text-white">
                <h5 className="mb-0"><i className="fas fa-clock me-2"></i>Password Expiration</h5>
              </div>
              <div className="card-body">
                <div className="d-flex flex-column gap-2">
                  <div className="d-flex justify-content-between">
                    <span className="text-danger">Expired</span>
                    <strong className="text-danger">{summary.expired_count || 0}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span className="text-warning">Expiring Soon (30 days)</span>
                    <strong className="text-warning">{summary.expiring_soon_count || 0}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span className="text-info">Never Expires</span>
                    <strong className="text-info">{summary.no_expiry_count || 0}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span className="text-success">Valid</span>
                    <strong className="text-success">{summary.valid_count || 0}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Roles Tab */}
      {activeTab === 'roles' && !loading && (
        <div className="card">
          <div className="card-header bg-light border-bottom d-flex justify-content-between align-items-center">
            <h5 className="mb-0"><i className="fas fa-users me-2"></i>Role Security Settings</h5>
            <div className="d-flex gap-2">
              <select
                className="form-select form-select-sm"
                style={{ width: '180px' }}
                value={filters.filter_type}
                onChange={(e) => setFilters({...filters, filter_type: e.target.value})}
              >
                <option value="all">All Roles</option>
                <option value="login">Login Roles Only</option>
                <option value="superuser">Superusers</option>
                <option value="high_risk">High Risk Roles</option>
                <option value="bypass_rls">Bypass RLS</option>
                <option value="no_expiry">No Password Expiry</option>
                <option value="expired">Expired Passwords</option>
                <option value="expiring_soon">Expiring Soon</option>
              </select>
              <div className="input-group input-group-sm" style={{ width: '200px' }}>
                <span className="input-group-text"><i className="fas fa-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search roles..."
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                />
              </div>
            </div>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-dark">
                  <tr>
                    <th>Database</th>
                    <th>Role Name</th>
                    <th>Type</th>
                    <th className="text-center">Login</th>
                    <th className="text-center">Superuser</th>
                    <th className="text-center">Create DB</th>
                    <th className="text-center">Create Role</th>
                    <th className="text-center">Bypass RLS</th>
                    <th>Conn Limit</th>
                    <th>Password Expires</th>
                    <th>Expiry Status</th>
                    <th>Risk</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {roles.length === 0 ? (
                    <tr>
                      <td colSpan="13" className="text-center py-4 text-muted">
                        No roles found matching the criteria
                      </td>
                    </tr>
                  ) : (
                    roles.map((role) => (
                      <tr key={role.id}>
                        <td><span className="text-muted">{role.database_name}</span></td>
                        <td><strong>{role.role_name}</strong></td>
                        <td><span className={role.role_type === 'USER' ? 'text-primary' : 'text-secondary'}>{role.role_type}</span></td>
                        <td className="text-center">
                          {role.can_login ? <i className="fas fa-check text-success"></i> : <span className="text-muted">-</span>}
                        </td>
                        <td className="text-center">
                          {role.is_superuser ? <i className="fas fa-crown text-danger"></i> : <span className="text-muted">-</span>}
                        </td>
                        <td className="text-center">
                          {role.can_create_db ? <i className="fas fa-check text-warning"></i> : <span className="text-muted">-</span>}
                        </td>
                        <td className="text-center">
                          {role.can_create_role ? <i className="fas fa-check text-warning"></i> : <span className="text-muted">-</span>}
                        </td>
                        <td className="text-center">
                          {role.bypass_rls ? <i className="fas fa-exclamation-triangle text-danger"></i> : <span className="text-muted">-</span>}
                        </td>
                        <td>{role.connection_limit === -1 ? 'Unlimited' : role.connection_limit}</td>
                        <td>
                          {role.valid_until ? new Date(role.valid_until).toLocaleDateString() : <span className="text-muted">Never</span>}
                        </td>
                        <td>{getExpiryText(role.expiry_status)}</td>
                        <td>{getRiskText(role.risk_level)}</td>
                        <td>
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => setSelectedRole(role)}
                            title="View Details"
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="card-footer bg-light d-flex justify-content-between align-items-center">
            <small className="text-muted">
              Showing {roles.length} of {totalCount} roles
            </small>
            {renderPagination()}
          </div>
        </div>
      )}

      {/* Security Guidelines Tab */}
      {activeTab === 'rules' && !loading && (
        <div className="card">
          <div className="card-header bg-light border-bottom">
            <h5 className="mb-0"><i className="fas fa-book me-2"></i>PostgreSQL Security Guidelines</h5>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-dark">
                  <tr>
                    <th style={{ width: '120px' }}>Code</th>
                    <th style={{ width: '200px' }}>Rule</th>
                    <th>Description</th>
                    <th style={{ width: '100px' }}>Severity</th>
                    <th>Recommendation</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((rule, idx) => (
                    <tr key={idx}>
                      <td><code>{rule.rule_code}</code></td>
                      <td><strong>{rule.rule_name}</strong></td>
                      <td className="small">{rule.description}</td>
                      <td>{getRiskText(rule.severity)}</td>
                      <td className="small text-muted">{rule.recommendation}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="card-footer bg-light">
            <small className="text-muted">
              <i className="fas fa-info-circle me-1"></i>
              PostgreSQL doesn't have built-in password profiles like Oracle. These guidelines are based on best practices.
              Consider using extensions like <code>passwordcheck</code> or <code>credcheck</code> for password complexity enforcement.
            </small>
          </div>
        </div>
      )}

      {/* Server Settings Tab */}
      {activeTab === 'settings' && !loading && (
        <div className="row">
          {serverSettings.map((db) => (
            <div className="col-md-6" key={db.id}>
              <div className="card mb-4">
                <div className="card-header bg-secondary text-white">
                  <h5 className="mb-0">
                    <i className="fas fa-server me-2"></i>
                    {db.db_name} <small className="opacity-75">({db.host}:{db.port})</small>
                  </h5>
                </div>
                <div className="card-body p-0">
                  <table className="table table-sm mb-0">
                    <tbody>
                      {Object.entries(db.settings || {})
                        .filter(([key, value]) => key !== 'credcheck_settings' && typeof value !== 'object')
                        .map(([key, value]) => (
                        <tr key={key}>
                          <td className="text-capitalize" style={{ width: '60%' }}>
                            {key.replace(/_/g, ' ')}
                          </td>
                          <td>
                            {value === 'scram-sha-256' && <span className="text-success"><i className="fas fa-check me-1"></i>{value}</span>}
                            {value === 'md5' && <span className="text-danger"><i className="fas fa-times me-1"></i>{value} (Weak)</span>}
                            {value === 'on' && <span className="text-success">{value}</span>}
                            {value === 'off' && <span className="text-muted">{value}</span>}
                            {value === 'Not Installed' && <span className="text-warning">{value}</span>}
                            {value === 'Unknown' && <span className="text-muted">{value}</span>}
                            {typeof value === 'string' && !['scram-sha-256', 'md5', 'on', 'off', 'Not Installed', 'Unknown'].includes(value) && (
                              <span className="text-success"><i className="fas fa-check me-1"></i>{value}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Credcheck Settings Section */}
                {db.settings?.credcheck_settings && Object.keys(db.settings.credcheck_settings).length > 0 && (
                  <div className="card-footer bg-light p-0">
                    <div className="p-2 bg-success bg-opacity-10 border-top">
                      <h6 className="mb-0 text-success">
                        <i className="fas fa-shield-alt me-2"></i>
                        Credcheck Password Policy
                      </h6>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-sm mb-0">
                        <tbody>
                          {Object.entries(db.settings.credcheck_settings)
                            .filter(([key]) => key.startsWith('password_'))
                            .map(([key, value]) => (
                            <tr key={key}>
                              <td className="text-capitalize ps-3" style={{ width: '60%' }}>
                                {key.replace('password_', '').replace(/_/g, ' ')}
                              </td>
                              <td>
                                {value === '0' || value === '' ? (
                                  <span className="text-muted">Not Set</span>
                                ) : value === 'on' ? (
                                  <span className="text-success"><i className="fas fa-check me-1"></i>Enabled</span>
                                ) : value === 'off' ? (
                                  <span className="text-muted">Disabled</span>
                                ) : (
                                  <span className="text-primary">{value}</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
          {serverSettings.length === 0 && (
            <div className="col-12">
              <div className="alert alert-info">
                <i className="fas fa-info-circle me-2"></i>
                No database settings available. Settings are collected during data refresh.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Role Details Modal */}
      <WideModal
        show={!!selectedRole}
        onClose={() => setSelectedRole(null)}
        title={`Role Details: ${selectedRole?.role_name || ''}`}
        headerIcon="fa-user-shield"
        headerClass="bg-primary text-white"
        maxWidth="800px"
        footer={
          <button className="btn btn-secondary" onClick={() => setSelectedRole(null)}>Close</button>
        }
      >
        {selectedRole && (
          <div className="row">
            <div className="col-md-6">
              <div className="card mb-3">
                <div className="card-header bg-light py-2">
                  <h6 className="mb-0"><i className="fas fa-info-circle me-2"></i>Role Information</h6>
                </div>
                <div className="card-body">
                  <table className="table table-sm mb-0">
                    <tbody>
                      <tr><th style={{ width: '40%' }}>Database</th><td>{selectedRole.database_name}</td></tr>
                      <tr><th>Host</th><td>{selectedRole.host}</td></tr>
                      <tr><th>Role Name</th><td><strong>{selectedRole.role_name}</strong></td></tr>
                      <tr><th>Role Type</th><td>{selectedRole.role_type}</td></tr>
                      <tr><th>Can Login</th><td>{selectedRole.can_login ? <span className="text-success">Yes</span> : 'No'}</td></tr>
                      <tr><th>Connection Limit</th><td>{selectedRole.connection_limit === -1 ? 'Unlimited' : selectedRole.connection_limit}</td></tr>
                      <tr><th>Password Expires</th><td>{selectedRole.valid_until ? new Date(selectedRole.valid_until).toLocaleString() : 'Never'}</td></tr>
                      <tr><th>Expiry Status</th><td>{getExpiryText(selectedRole.expiry_status)}</td></tr>
                      <tr><th>Risk Level</th><td>{getRiskText(selectedRole.risk_level)}</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card mb-3">
                <div className="card-header bg-light py-2">
                  <h6 className="mb-0"><i className="fas fa-shield-alt me-2"></i>Security Attributes</h6>
                </div>
                <div className="card-body">
                  <table className="table table-sm mb-0">
                    <tbody>
                      <tr>
                        <th style={{ width: '50%' }}>Superuser</th>
                        <td>
                          {selectedRole.is_superuser
                            ? <span className="text-danger"><i className="fas fa-crown me-1"></i>Yes</span>
                            : <span className="text-muted">No</span>}
                        </td>
                      </tr>
                      <tr>
                        <th>Bypass RLS</th>
                        <td>
                          {selectedRole.bypass_rls
                            ? <span className="text-danger"><i className="fas fa-exclamation-triangle me-1"></i>Yes</span>
                            : <span className="text-muted">No</span>}
                        </td>
                      </tr>
                      <tr>
                        <th>Create Database</th>
                        <td>
                          {selectedRole.can_create_db
                            ? <span className="text-warning">Yes</span>
                            : <span className="text-muted">No</span>}
                        </td>
                      </tr>
                      <tr>
                        <th>Create Role</th>
                        <td>
                          {selectedRole.can_create_role
                            ? <span className="text-warning">Yes</span>
                            : <span className="text-muted">No</span>}
                        </td>
                      </tr>
                      <tr>
                        <th>Replication</th>
                        <td>
                          {selectedRole.is_replication
                            ? <span className="text-info">Yes</span>
                            : <span className="text-muted">No</span>}
                        </td>
                      </tr>
                      <tr>
                        <th>Role Memberships</th>
                        <td>{selectedRole.membership_count || 0}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </WideModal>
    </div>
  );
};

export default PostgresqlPasswordSecurity;
